'use client';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Link, SxProps, Typography, useMediaQuery } from '@mui/material';
import { MouseEvent, useCallback } from 'react';
import { DisclosureTooltip } from '../../../DisclosureTooltip';
import { StarIcon } from '../../../StarRating/components/StarIcon';
import { scrollToViewPromise } from '../../../TableOfContents/scrollToViewPromise';
import { theme } from '../../../Theme';
import { MOBILE_SCROLL_OFFSET } from '../OverviewPanel/constants';

type Layout = 'row' | 'column';

type Props = {
  value?: number;
  withBackground?: boolean;
  layout?: Layout;
  showValue?: boolean;
  showStar?: boolean;
  showLabel?: boolean;
  labelText?: string;
  tooltipText?: string;
  href?: string;
  sectionId?: string;
  sx?: SxProps;
};

export function StarRating({
  value,
  withBackground = false,
  layout = 'row',
  showValue = true,
  showStar = true,
  showLabel = false,
  labelText = 'NerdWallet Rating',
  tooltipText,
  href,
  sectionId = 'rating-methodology-section',
  sx,
}: Props) {
  if (!value) return null;

  const isColumn = layout === 'column';
  const resolvedHref = href ?? (withBackground ? `#${sectionId}` : undefined);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      if (!resolvedHref || !resolvedHref.startsWith('#')) return;
      
      event.preventDefault();
      const anchorId = resolvedHref.replace('#', '');
      const scrollOffset = isMobile ? MOBILE_SCROLL_OFFSET : 0;
      await scrollToViewPromise(anchorId, 'smooth', scrollOffset, true);
    },
    [resolvedHref, isMobile],
  );

  const Root: React.ElementType = withBackground && resolvedHref ? Box : Box;
  const rootProps =
    withBackground && resolvedHref
      ? { component: 'a', href: resolvedHref, onClick: handleClick }
      : {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
      {showLabel && withBackground && (
        <Typography
          variant="textSmall"
          color="neutral.darker"
          sx={{ display: { xs: 'flex', md: 'none'}, textWrap: 'nowrap' }}
        >
          {labelText}
        </Typography>
      )}
      <Root
        {...rootProps}
        data-testid="product-card-rating"
        sx={{
          display: 'flex',
          flexDirection: isColumn ? 'column' : 'row',
          alignItems: 'center',
          gap: 1,

          ...(withBackground && resolvedHref
            ? {
              borderRadius: 2,
              backgroundColor: 'greens.lightest',
              padding: 2,
              cursor: 'pointer',
              color: 'black',
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: 'greens.lighter',
                color: 'black',
                textDecoration: 'none',
              },
            }
            : {}),

          ...sx,
        }}
      >
        {(showValue || showStar) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showValue && (
              <Typography
                variant="textBold"
                sx={{
                  lineHeight: '100%',
                  fontSize: withBackground ? '20px' : '14px',
                  textDecoration: 'none',
                  color: 'neutral.darkest',
                  '&:hover': {
                    color: 'neutral.darkest',
                    textDecoration: 'none',
                  },
                }}
              >
                {value.toFixed(1)}
              </Typography>
            )}
            {showStar && (
              <StarIcon color="#008254" additionalStyles={{ width: '18px', height: '18px' }} />
            )}
          </Box>
        )}

        {showLabel && !withBackground && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {resolvedHref ? (
              <Typography
                component="a"
                href={resolvedHref}
                onClick={handleClick}
                variant="textSmall"
                sx={{
                  textWrap: 'nowrap',
                  marginRight: 1,
                  color: 'blues.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {labelText}
              </Typography>
            ) : (
              <Typography
                variant="textSmall"
                sx={{
                  textWrap: 'nowrap',
                  marginRight: 1,
                  color: 'neutral.dark',
                }}
              >
                {labelText}
              </Typography>
            )}

            {tooltipText ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <DisclosureTooltip
                  tooltipStyleOverride={{ lineHeight: 1, maxWidth: '265px' }}
                  trigger={
                    <Link
                      aria-label="NerdWallet rating info"
                      component="button"
                      lineHeight={1}
                      color="text.primary"
                    >
                      <HelpOutlineIcon
                        sx={{
                          color: 'neutral.dark',
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </Link>
                  }
                >
                  <Typography variant="textSmall" color="neutral.dark">
                    {tooltipText}
                  </Typography>
                </DisclosureTooltip>
              </Box>
            ) : null}
          </Box>
        )}
      </Root>
    </Box>
  );
}
